import React, { useState, useContext } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import usersRequest from "../../request/usersRequest";
import { CombinedContext } from "../../context/combinedContext";
import translations from "../../translations/translations";
import "./confirmationCode.css";

const ConfirmCodePage = () => {
	const [searchParams] = useSearchParams();
	const [email, setEmail] = useState(searchParams.get("email") || "");
	const [code, setCode] = useState(searchParams.get("code") || "");

	const [loading, setLoading] = useState(false);
	const [error, setError] = useState("");
	const [invalidFields, setInvalidFields] = useState(new Set());

	const { language } = useContext(CombinedContext);
	const navigate = useNavigate();

	/* The message says what's wrong; these say where. A flagged field clears
	   as soon as it's touched, so the red only ever marks what's still open. */
	const clearInvalid = (field) => {
		setInvalidFields((prev) => {
			if (!prev.has(field)) return prev;
			const next = new Set(prev);
			next.delete(field);
			return next;
		});
	};

	const invalidClass = (field) => (invalidFields.has(field) ? " is-invalid" : "");

	const handleConfirm = async () => {
		const missing = new Set();
		if (!email) missing.add("email");
		if (!code) missing.add("code");

		if (missing.size > 0) {
			setInvalidFields(missing);
			setError(language === "es" ? "Completa tu correo y el código para continuar." : "Enter your email and the code to continue.");
			return;
		}

		setInvalidFields(new Set());
		setError("");
		setLoading(true);

		try {
			await usersRequest.confirmSignUp({ email, code });
			navigate("/signin");
		} catch (err) {
			console.error("Confirmation failed:", err);
			setError(err.message || translations[language].confirmationCode.invalidCode);
		} finally {
			setLoading(false);
		}
	};

	const handleKeyDown = (e) => {
		if (e.key === "Enter") handleConfirm();
	};

	const t = translations[language].confirmationCode;

	return (
		<div className="sf-auth-page sf-cc-root">
			<div className="sf-auth-shell">
				<div className="sf-auth-card">
					<div className="sf-auth-head">
						<h1 className="sf-auth-title">{t.title}</h1>
						<p className="sf-auth-sub">{t.subtitle}</p>
					</div>

					<div className="sf-auth-field">
						<label className="sf-auth-label">{t.email}</label>
						<input
							className={`sf-auth-input${invalidClass("email")}`}
							type="email"
							placeholder={t.email}
							value={email}
							onChange={(e) => {
								setEmail(e.target.value);
								clearInvalid("email");
							}}
							autoComplete="email"
						/>
					</div>

					<div className="sf-auth-field">
						<label className="sf-auth-label">{t.code}</label>
						<input
							className={`sf-auth-input sf-cc-code${invalidClass("code")}`}
							type="text"
							placeholder="••••••"
							value={code}
							onChange={(e) => {
								setCode(e.target.value);
								clearInvalid("code");
							}}
							onKeyDown={handleKeyDown}
							autoComplete="one-time-code"
							inputMode="numeric"
							maxLength={10}
						/>
						<p className="sf-auth-hint">{t.hint}</p>
					</div>

					{error && <p className="sf-cc-error">{error}</p>}

					<button className="sf-auth-cta" onClick={handleConfirm} disabled={loading}>
						{loading && <span className="sf-auth-spinner" aria-hidden="true" />}
						{loading ? t.loading : t.button}
					</button>

					<p className="sf-auth-foot">
						<Link to="/signin">{t.backToLogin}</Link>
					</p>
				</div>
			</div>
		</div>
	);
};

export default ConfirmCodePage;
